// ===========================================
// Signed slip PDF download
// ===========================================
//
// A plain <a href> CANNOT fetch these, for two independent reasons:
//   1. The school slug and /api/v1 prefix are injected by the axios request
//      interceptor. A browser navigation bypasses that and lands on the SPA
//      catch-all, so the user gets index.html in a new tab — a blank app, not
//      a PDF, with no error to explain it.
//   2. Auth is a Bearer token from localStorage, not a cookie. A navigation
//      cannot carry it, so the (correctly) auth-gated route 401s.
//
// Fetching through the api client solves both; the Blob then becomes a
// short-lived object URL. Revoked immediately after use — these documents
// contain medical detail and should not linger in the object-URL table.

/**
 * Open a signed permission slip PDF in a new tab.
 *
 * @param fetcher  the api call that returns the PDF as a Blob
 * @param onError  surfaced to the user; a silent failure here looks like a
 *                 broken button
 */
export async function openSignedSlipPdf(
  fetcher: () => Promise<Blob>,
  onError: (message: string) => void
): Promise<void> {
  try {
    const blob = await fetcher();
    const url = URL.createObjectURL(blob);
    const win = window.open(url, '_blank', 'noopener,noreferrer');
    if (!win) {
      // Popup blocked — fall back to a same-tab download rather than failing
      // silently, which is what a blocked window otherwise looks like.
      const a = document.createElement('a');
      a.href = url;
      a.download = 'permission-slip.pdf';
      a.click();
    }
    // Give the tab a moment to take the blob before releasing it.
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  } catch {
    onError('Could not open the signed permission slip. Please try again.');
  }
}
